import React, { useEffect, useState } from "react";
import { Row, Col, Form, Divider } from "antd";
import PrimaryButton from "../../../../biblio/Buttons/PrimaryButton";
import Selects from "../../../../biblio/Selects/Selects";
import UploadInput from "../../../../biblio/Inputs/UploadInput";
import { convertFileToBase64 } from "../../../../components/utils/utils";
import {
  UploadOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../apis/store";
import {
  getBeneficiaryDetails,
  addProofDocument,
  getProofDocuments,
} from "../../../../apis/actions/recipient.actions";
import ar_EG from "antd/lib/locale/ar_EG";
import { ConfigProvider } from "antd";
import { showNotitfication } from "../../../../components/utils/utils";
interface Props {
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProofDocumentRequest = ({ setIsModalVisible }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const [uploadedFiles, setUploadedFiles] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [members, setMembers] = useState<
    Array<{ id: number; member_id?: string; identify_image_ids?: any[] }>
  >([]);
  const beneficiaries = useSelector(
    (state: RootState) => state?.recipient?.beneficiaryDetails
  );
  useEffect(() => {
    dispatch(getBeneficiaryDetails());
  }, [dispatch]);

  const handleFileChange = async (fileList: any[], fieldName: string) => {
    if (fileList.length > 0) {
      try {
        const filesData = await Promise.all(
          fileList.map(async (fileItem: any) => {
            const file = fileItem.originFileObj;
            const base64 = (await convertFileToBase64(file)) as string;
            return {
              filename: file.name,
              data: base64,
            };
          })
        );

        setUploadedFiles((prev: any) => ({
          ...prev,
          [fieldName]: filesData,
        }));
      } catch (error) {
        console.error("Error converting file to Base64:", error);
      }
    } else {
      setUploadedFiles((prev: any) => ({
        ...prev,
        [fieldName]: [],
      }));
    }
  };
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const proofDocumentsData = {
        attachment_family_ids: uploadedFiles.attachment_family_ids || [],
        attachment_members: members.map((member) => ({
          member_id: values[`member_${member.id}_id`]?.value,
          identify_image_ids:
            uploadedFiles[`member_${member.id}_identify_image_ids`] || [],
        })),
      };

      dispatch(addProofDocument(proofDocumentsData))
        .then((result: any) => {
          const response = result?.payload?.data?.result;
          if (response?.code === 200) {
            showNotitfication(response?.message, "success");
            setIsModalVisible(false);
            dispatch(getProofDocuments());
          } else {
            showNotitfication(response?.message, "error");
          }
        })
        .finally(() => {
          setLoading(false); // ✅ Now runs after the promise completes
        });
    } catch (error) {
      console.error(error);
      setLoading(false); // ✅ Also set to false if validation fails
    }
  };
  const addMember = () => {
    setMembers((prev) => [...prev, { id: Date.now() }]);
  };

  const removeMember = (id: number) => {
    setMembers((prev) => prev.filter((member) => member.id !== id));
    const updatedUploadedFiles = { ...uploadedFiles };
    delete updatedUploadedFiles[`member_${id}_identify_image_ids`];
    setUploadedFiles(updatedUploadedFiles);
  };

  return (
    <ConfigProvider locale={ar_EG} direction="rtl">
      <Row justify={"center"} style={{ paddingBottom: "2rem" }}>
        <p className="takaful--confirm-logout">إضافة أوراق الثبوتية</p>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changedValues) => {
          const changedKeys = Object.keys(changedValues);
          const isMemberFieldChanged = changedKeys.some((key) =>
            key.startsWith("member_")
          );

          if (isMemberFieldChanged) {
            form.validateFields(["attachment_family_ids"]);
          }
        }}
      >
        <Row gutter={8} dir="rtl">
          {/* Family proof documents */}
          <Col xxl={16} xl={16} lg={16} md={16} sm={24} xs={24}>
            <Form.Item
              name="attachment_family_ids"
              rules={[
                {
                  validator: (_, value) => {
                    if (
                      uploadedFiles?.attachment_family_ids?.length > 0 ||
                      members?.length > 0
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        "الرجاء إرفاق أوراق ثبوتية للأسرة أو أوراق ثبوتية للأفراد ."
                      )
                    );
                  },
                },
              ]}
            >
              <Row align="middle" gutter={[8, 16]}>
                {/* Label */}

                <Col span={10}>
                  <label className="takaful--content-services-request-p">
                    أوراق ثبوتية للأسرة
                  </label>
                </Col>

                {/* Input */}
                <Col span={14}>
                  <UploadInput
                    accept="*"
                    text="أوراق ثبوتية للأسرة"
                    icon={<UploadOutlined />}
                    multiple={true}
                    maxCount={5}
                    onChange={(fileList) => {
                      handleFileChange(fileList, "attachment_family_ids");
                      form.setFieldsValue({
                        attachment_family_ids:
                          uploadedFiles.attachment_family_ids,
                      });
                    }}
                  />
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>

        <Row dir="rtl">
          <Col xxl={22} xl={22} lg={22} md={22} sm={22} xs={22}>
            <Divider className="takaful--service-section-divider" />
          </Col>
        </Row>
        <Row justify={"end"} style={{ paddingBottom: "2rem" }}>
          <p className="takaful--content-services-request-p">
            إضافة أوراق ثبوتية للأفراد
          </p>
        </Row>
        {/* Member documents */}
        {members.map((member) => (
          <Row gutter={16} key={member.id} dir="rtl">
            <Col xxl={10} xl={10} lg={10} md={10} sm={24} xs={24}>
              <Form.Item
                name={`member_${member.id}_id`}
                label="الفرد"
                rules={[{ required: true, message: "الرجاء تعبئة الحقل" }]}
                labelCol={{ className: "takaful--label-style" }}
              >
                <Selects
                  options={
                    Array.isArray(beneficiaries?.person_ids)
                      ? beneficiaries.person_ids.map((person: any) => ({
                          id: person.id,
                          name: person.name,
                        }))
                      : []
                  }
                  selectPlaceholder="الفرد"
                />
              </Form.Item>
            </Col>

            {/* Member identification documents */}
            <Col xxl={10} xl={10} lg={10} md={10} sm={20} xs={20}>
              <Form.Item
                name={`member_${member.id}_identify_image_ids`}
                label="أوراق الثبوتية"
                rules={[{ required: true, message: "الرجاء تعبئة الحقل" }]}
                labelCol={{ className: "takaful--label-style" }}
              >
                <UploadInput
                  accept="*"
                  text="أوراق الثبوتية"
                  icon={<UploadOutlined />}
                  multiple={false}
                  maxCount={1}
                  onChange={(fileList) =>
                    handleFileChange(
                      fileList,
                      `member_${member.id}_identify_image_ids`
                    )
                  }
                />
              </Form.Item>
            </Col>
            <Col span={2} style={{ textAlign: "center", padding: "1.6rem" }}>
              <PrimaryButton
                title=""
                icon={<DeleteOutlined />}
                onClick={() => removeMember(member.id)}
              />
            </Col>
            {/* Remove member */}
          </Row>
        ))}
      </Form>

      {/* Add member */}
      <Row>
        <Col
          span={24}
          style={{ direction: "rtl" }}
          className="takaful--button-material"
        >
          <PrimaryButton
            title="إضافة أوراق ثبوتية للفرد"
            icon={<PlusCircleOutlined />}
            onClick={addMember}
            className="takaful--primary-button-info"
          />
        </Col>
      </Row>

      {/* Submit button */}
      <Row style={{ marginTop: "1rem" }}>
        <Col span={24} style={{ textAlign: "start" }}>
          <PrimaryButton
            title="إرسال"
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          />
        </Col>
      </Row>
    </ConfigProvider>
  );
};

export default ProofDocumentRequest;
